import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { dedupedRequest, invalidateCacheByUrl } from '../../../utils/axios';
import LogoUpload from './LogoUpload';
import EditIcon from '../../icons/EditIcon';
import ToggleOn from '../../icons/ToggleOn';
import ToggleOff from '../../icons/ToggleOff';

type Topic = {
  id: number;
  name: string;
  selected: boolean;
};

type ClientPayload = {
  name?: string;
  activity_trade?: string | null;
  activity_description?: string | null;
  employees?: number | null;
  annual_turnover?: string | null;
  standards?: string | null;
  country_province?: string | null;
  comments?: string | null;
  topics?: number[];
  show_tasks?: boolean;
  show_decisions?: boolean;
  show_colors?: boolean;
};

// Internal form state used by the UI:
type FormState = Omit<ClientPayload, 'topics'> & {
  topics?: Topic[];                 // <-- UI uses full objects with `selected`
};

interface ClientModalProps {
  show: boolean;
  ClientId: number;
  c: any;
  onClose: () => void;
  onSave: () => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ show, ClientId, c, onClose, onSave }) => {
  const [form, setForm] = useState<FormState>({ topics: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showTasks, setShowTasks] = useState(c?.show_tasks ?? false);
  const [showDecisions, setShowDecisions] = useState(c?.show_decisions ?? false);
  const [showColors, setShowColors] = useState(c?.show_colors ?? false);

  useEffect(() => {
    if (!c) return;
    setForm({
      name: c.name ?? '',
      activity_trade: c.activity_trade ?? '',
      activity_description: c.activity_description ?? '',
      employees: c.employees ?? null,
      annual_turnover: c.annual_turnover ?? '',
      standards: c.standards ?? '',
      country_province: c.country_province ?? '',
      comments: c.comments ?? '',
      topics: seedFromClient(c.topics ?? []),
      show_tasks: c.show_tasks ?? false,
      show_decisions: c.show_decisions ?? false,
      show_colors: c.show_colors ?? true
    });
    // console.log("Client data loaded into form:", c);
    loadAllTopics();
  }, [c]);

// Seed from the client's existing topics: mark them selected
  const seedFromClient = (clientTopics: Array<{ id: number; name: string }> = []): Topic[] =>
    clientTopics.map(t => ({ id: t.id, name: t.name, selected: true }));

  // Merge available topics with a set of selected IDs
  const mergeAvailable = (
    available: Array<{ id: number; name: string }>,
    selectedIds: Set<number>
  ): Topic[] =>
    available.map(a => ({ id: a.id, name: a.name, selected: selectedIds.has(a.id) }));

  const handleTopicsEditBtnClick = () => {
    loadAllTopics();
    setShowModal(true);
  };

  const logoUrl = c && c.logo_filename ? `/api/clients/${c.id}/logo` : null;

  const loadAllTopics = async () => {
    try {
      const res = await dedupedRequest({ method: 'GET', url: '/admin/topics' }, { useCache: false });
      const available: Array<{ id: number; name: string }> = res.data;

      setForm(prev => {
        const selectedIds = new Set((prev.topics ?? []).filter(t => t.selected).map(t => t.id));
        return { ...prev, topics: mergeAvailable(available, selectedIds) };
      });
    } catch (err) {
      console.error('Failed to load topics', err);
    }
  };

  // Toggle by id
  const updateTopic = (t: Topic, selected: boolean) => () => {
    setForm(prev => ({
      ...prev,
    topics: (prev.topics ?? []).map(x => (x.id === t.id ? { ...x, selected } : x)),
    }));
  };

  const updateField =
    (key: keyof ClientPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value;
      setForm(prev => ({ ...prev, [key]: value }));
    };

  const updateShowTasks = (value: boolean) => {
    setShowTasks(value);
    setForm(prev => ({
      ...prev,
      show_tasks: value,
    }));
  };

  const updateShowDecisions = (value: boolean) => {
    setShowDecisions(value);
    setForm(prev => ({
      ...prev,
      show_decisions: value,
    }));
  };

  const updateShowColors = (value: boolean) => {
    setShowColors(value);
    setForm(prev => ({
      ...prev,
      show_colors: value,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Only send fields the user actually filled (optional)
    const { topics, ...rest } = form;
    const payload: ClientPayload = {
      ...rest,
      topics: (topics ?? []).filter(t => t.selected).map(t => t.id), // <-- only IDs
    };

    try {
      await dedupedRequest(
        { method: 'PUT', url: `/admin/clients/${ClientId}`, data: payload },
        { useCache: false, maxRetries: 2 }
      );

      // Bust cached GET /admin/clients so the list refreshes correctly
      invalidateCacheByUrl('/admin/clients');

      onSave();   // triggers parent to reload
      onClose();  // close the modal
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.errors) && 'Validation error') ||
        'Speichern fehlgeschlagen.';
      setError(msg);
      console.error("Error saving client:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Kunde bearbeiten</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control value={form.name ?? ''} onChange={updateField('name')} required />
          </Form.Group>
          <Form.Group>
            <Form.Label>Logo</Form.Label>
            <div>{logoUrl && <img src={logoUrl} alt="Logo" style={{ height: 28, objectFit: 'contain', marginRight: '2rem' }} />}</div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Tätigkeitsfeld/Gewerbe</Form.Label>
            <Form.Control value={form.activity_trade ?? ''} onChange={updateField('activity_trade')} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Tätigkeitsbeschreibung</Form.Label>
            <Form.Control as="textarea" value={form.activity_description ?? ''} onChange={updateField('activity_description')} />
          </Form.Group>
          <div className="grid-container">
            <Form.Group>
              <Form.Label>Mitarbeiteranzahl</Form.Label>
              <Form.Control type="number" value={form.employees ?? ''} onChange={updateField('employees')} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Jahresumsatz</Form.Label>
              <Form.Control value={form.annual_turnover ?? ''} onChange={updateField('annual_turnover')} />
            </Form.Group>
          </div>
          <Form.Group>
            <Form.Label>Normen</Form.Label>
            <Form.Control value={form.standards ?? ''} onChange={updateField('standards')} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Land/Bundesland</Form.Label>
            <Form.Control value={form.country_province ?? ''} onChange={updateField('country_province')} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Module</Form.Label>
            <div className="grid-container">
              <span className='toggle-btn' onClick={() => updateShowTasks(!showTasks)} title='Maßnahmen'>
                {showTasks ? <ToggleOn /> : <ToggleOff />} Maßnahmen
              </span>
              <span className='toggle-btn' onClick={() => updateShowDecisions(!showDecisions)} title='Bescheidregister'>
                {showDecisions ? <ToggleOn /> : <ToggleOff />} Bescheidregister
              </span>
              <span className='toggle-btn' onClick={() => updateShowColors(!showColors)} title='Farben'>
                {showColors ? <ToggleOn /> : <ToggleOff />} Farben
              </span>
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>
              <Button className='action-btn' size="sm" onClick={handleTopicsEditBtnClick}>
                <EditIcon />
              </Button>
              Themen:</Form.Label>
              {(form.topics ?? []).filter(t => t.selected).map(t => (
                <div key={t.id}>{t.name}</div>
              ))}
          </Form.Group>
          <Form.Group>
            <Form.Label>Anmerkungen</Form.Label>
            <Form.Control as="textarea" value={form.comments ?? ''} onChange={updateField('comments')} />
          </Form.Group>
          <div style={{ height: '1rem' }} />
          <Form.Group>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (<><Spinner size="sm" animation="border" /> Speichern…</>) : 'Speichern'}
            </Button>
          </Form.Group>
          <hr />
          {error && (
            <Form.Group>
              <Form.Label>Fehlermeldung:</Form.Label>
              <div style={{ color: 'red' }}>{error}</div>
            </Form.Group>
          )}
          <LogoUpload clientId={ClientId} />
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Form>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Themenauswahl:</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(form.topics ?? []).map((topic) => (
            <div key={topic.id}>
              <Form.Check
                type="checkbox"
                label={topic.name}
                id={`topic-${topic.id}`}
                checked={!!topic.selected}
                onChange={updateTopic(topic, !topic.selected)}
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default ClientModal;
